import * as THREE from 'three';
import { BLOCKS, getBlockMaterial } from './blocks';
import type { PortalSyncEntry } from './protocol';
import { computeChunkLight, LIGHT_MULTIPLIER } from './lightmap';

export const CHUNK_SIZE = 16;
export const CHUNK_HEIGHT = 512;
const SEA_LEVEL = 202;

export class Chunk {
  blocks: Uint8Array;
  lightData: Uint8Array;
  mesh: THREE.Group | null = null;
  cx: number;
  cz: number;
  dirty = true;
  lightDirty = true;
  maxY = 0;

  constructor(cx: number, cz: number) {
    this.cx = cx;
    this.cz = cz;
    this.blocks = new Uint8Array(CHUNK_SIZE * CHUNK_HEIGHT * CHUNK_SIZE);
    this.lightData = new Uint8Array(CHUNK_SIZE * CHUNK_HEIGHT * CHUNK_SIZE);
  }

  getBlock(x: number, y: number, z: number): number {
    if (x < 0 || x >= CHUNK_SIZE || y < 0 || y >= CHUNK_HEIGHT || z < 0 || z >= CHUNK_SIZE) return 0;
    return this.blocks[x + z * CHUNK_SIZE + y * CHUNK_SIZE * CHUNK_SIZE];
  }

  setBlock(x: number, y: number, z: number, blockId: number) {
    if (x < 0 || x >= CHUNK_SIZE || y < 0 || y >= CHUNK_HEIGHT || z < 0 || z >= CHUNK_SIZE) return;
    this.blocks[x + z * CHUNK_SIZE + y * CHUNK_SIZE * CHUNK_SIZE] = blockId;
    if (blockId !== 0 && y > this.maxY) this.maxY = y;
    this.dirty = true;
  }

  getSkyLight(x: number, y: number, z: number): number {
    if (x < 0 || x >= CHUNK_SIZE || y < 0 || y >= CHUNK_HEIGHT || z < 0 || z >= CHUNK_SIZE) return 15;
    return (this.lightData[x + z * CHUNK_SIZE + y * CHUNK_SIZE * CHUNK_SIZE] >> 4) & 0xf;
  }

  getBlockLight(x: number, y: number, z: number): number {
    if (x < 0 || x >= CHUNK_SIZE || y < 0 || y >= CHUNK_HEIGHT || z < 0 || z >= CHUNK_SIZE) return 0;
    return this.lightData[x + z * CHUNK_SIZE + y * CHUNK_SIZE * CHUNK_SIZE] & 0xf;
  }
}

// Callback for when the client modifies a block (so main.ts can send to server)
export type BlockModifyCallback = (wx: number, wy: number, wz: number, blockId: number) => void;

// Shared materials (created once, reused across all chunks)
const opaqueVertexMat = new THREE.MeshLambertMaterial({ vertexColors: true });
const specialMats: Record<string, THREE.MeshLambertMaterial> = {
  '20-stick': new THREE.MeshLambertMaterial({ color: 0x8b6c3a }),
  '20-flame': new THREE.MeshLambertMaterial({ color: 0xffcc00, emissive: 0xff8800, emissiveIntensity: 0.8 }),
  '44-arrow': new THREE.MeshLambertMaterial({ color: 0xffffff, emissive: 0x66ffcc, emissiveIntensity: 0.5 }),
  '45-arrow': new THREE.MeshLambertMaterial({ color: 0xffffff, emissive: 0xff66cc, emissiveIntensity: 0.8 }),
  '50-symbol': new THREE.MeshLambertMaterial({ color: 0xffffff, emissive: 0xff4444, emissiveIntensity: 0.8 }),
  '55-arrow': new THREE.MeshLambertMaterial({ color: 0xffffff, emissive: 0xff8800, emissiveIntensity: 0.6 }),
  '59-symbol': new THREE.MeshLambertMaterial({ color: 0xffffff, emissive: 0xcc66ff, emissiveIntensity: 0.6 }),
  '60-symbol': new THREE.MeshLambertMaterial({ color: 0xffffff, emissive: 0x00ccff, emissiveIntensity: 0.6 }),
  '61-symbol': new THREE.MeshLambertMaterial({ color: 0xffffff, emissive: 0xff0000, emissiveIntensity: 0.8 }),
  '62-symbol': new THREE.MeshLambertMaterial({ color: 0xffffff, emissive: 0xff6600, emissiveIntensity: 0.6 }),
  '63-symbol': new THREE.MeshLambertMaterial({ color: 0xffffff, emissive: 0x00ff88, emissiveIntensity: 0.6 }),
  'paint-51': new THREE.MeshLambertMaterial({ color: 0xff3333, emissive: 0xff3333, emissiveIntensity: 0.4, transparent: true, opacity: 0.6, side: THREE.DoubleSide }),
  'paint-52': new THREE.MeshLambertMaterial({ color: 0x3333ff, emissive: 0x3333ff, emissiveIntensity: 0.4, transparent: true, opacity: 0.6, side: THREE.DoubleSide }),
  'paint-53': new THREE.MeshLambertMaterial({ color: 0x33ff33, emissive: 0x33ff33, emissiveIntensity: 0.4, transparent: true, opacity: 0.6, side: THREE.DoubleSide }),
  'paint-54': new THREE.MeshLambertMaterial({ color: 0xffff33, emissive: 0xffff33, emissiveIntensity: 0.4, transparent: true, opacity: 0.6, side: THREE.DoubleSide }),
};

// Portal fill materials — one per color
const PORTAL_COLORS = [0x9b59b6, 0xe67e22, 0x1abc9c, 0x2ecc71, 0xe74c3c, 0xf1c40f, 0x3498db, 0xe84393];
const portalMats: THREE.MeshLambertMaterial[] = PORTAL_COLORS.map(c =>
  new THREE.MeshLambertMaterial({
    color: c,
    emissive: c,
    emissiveIntensity: 0.6,
    transparent: true,
    opacity: 0.7,
    side: THREE.DoubleSide,
  })
);
const portalMatDefault = new THREE.MeshLambertMaterial({
  color: 0x9b59b6,
  emissive: 0x9b59b6,
  emissiveIntensity: 0.6,
  transparent: true,
  opacity: 0.7,
  side: THREE.DoubleSide,
});

export class World {
  chunks: Map<string, Chunk> = new Map();
  scene: THREE.Scene;
  seed: number;
  onBlockModify: BlockModifyCallback | null = null;
  portalData: PortalSyncEntry[] = [];
  skyBrightness: number = 1.0;

  // Track which chunks have been requested from the server
  private requestedChunks = new Set<string>();
  // Callback to request a chunk from the server
  onRequestChunk: ((cx: number, cz: number) => void) | null = null;

  constructor(scene: THREE.Scene, seed: number = 12345) {
    this.scene = scene;
    this.seed = seed;
  }

  private computeLight(chunk: Chunk): void {
    computeChunkLight(chunk, (cx, cz) => this.getChunk(cx, cz));
  }

  chunkKey(cx: number, cz: number): string {
    return `${cx},${cz}`;
  }

  getChunk(cx: number, cz: number): Chunk | undefined {
    return this.chunks.get(this.chunkKey(cx, cz));
  }

  getBlock(wx: number, wy: number, wz: number): number {
    const cx = Math.floor(wx / CHUNK_SIZE);
    const cz = Math.floor(wz / CHUNK_SIZE);
    const chunk = this.getChunk(cx, cz);
    if (!chunk) return 0;
    const lx = ((wx % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    const lz = ((wz % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    return chunk.getBlock(lx, wy, lz);
  }

  setBlock(wx: number, wy: number, wz: number, blockId: number, sendToServer = true) {
    const cx = Math.floor(wx / CHUNK_SIZE);
    const cz = Math.floor(wz / CHUNK_SIZE);
    const chunk = this.getChunk(cx, cz);
    if (!chunk) return;
    const lx = ((wx % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    const lz = ((wz % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    chunk.setBlock(lx, wy, lz, blockId);
    chunk.lightDirty = true;

    if (sendToServer && this.onBlockModify) {
      this.onBlockModify(wx, wy, wz, blockId);
    }

    // Mark neighboring chunks dirty if on edge
    if (lx === 0) { const n = this.getChunk(cx - 1, cz); if (n) { n.dirty = true; n.lightDirty = true; } }
    if (lx === CHUNK_SIZE - 1) { const n = this.getChunk(cx + 1, cz); if (n) { n.dirty = true; n.lightDirty = true; } }
    if (lz === 0) { const n = this.getChunk(cx, cz - 1); if (n) { n.dirty = true; n.lightDirty = true; } }
    if (lz === CHUNK_SIZE - 1) { const n = this.getChunk(cx, cz + 1); if (n) { n.dirty = true; n.lightDirty = true; } }
  }

  // Receive chunk data from the server
  receiveChunk(cx: number, cz: number, blocks: Uint8Array, maxY: number) {
    const key = this.chunkKey(cx, cz);
    let chunk = this.chunks.get(key);
    if (!chunk) {
      chunk = new Chunk(cx, cz);
      this.chunks.set(key, chunk);
    }
    chunk.blocks = blocks;
    chunk.maxY = maxY;
    chunk.dirty = true;
    chunk.lightDirty = true;
    this.requestedChunks.delete(key);
  }

  buildChunkMesh(chunk: Chunk) {
    // Recompute light lazily — only when needed, just before mesh rebuild
    if (chunk.lightDirty) {
      this.computeLight(chunk);
      chunk.lightDirty = false;
    }

    if (chunk.mesh) {
      this.scene.remove(chunk.mesh);
      chunk.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
        }
      });
    }

    const group = new THREE.Group();
    group.position.set(chunk.cx * CHUNK_SIZE, 0, chunk.cz * CHUNK_SIZE);

    // Merged opaque geometry (vertex colors) — single draw call
    const opaquePos: number[] = [];
    const opaqueNorm: number[] = [];
    const opaqueColor: number[] = [];
    const opaqueIdx: number[] = [];

    // Separate buckets for transparent blocks and special geometry
    const specialBuckets: Map<string, { positions: number[], normals: number[], indices: number[] }> = new Map();

    function getSpecialBucket(key: string) {
      if (!specialBuckets.has(key)) {
        specialBuckets.set(key, { positions: [], normals: [], indices: [] });
      }
      return specialBuckets.get(key)!;
    }

    const tmpColor = new THREE.Color();
    const skyBri = this.skyBrightness;

    // Look up light at a local position, crossing chunk boundaries if needed
    const getLightAt = (lx: number, ly: number, lz: number): number => {
      if (ly < 0 || ly >= CHUNK_HEIGHT) {
        return ly >= CHUNK_HEIGHT ? 15 : 0; // above world = full sky, below = dark
      }
      let skyL: number, blockL: number;
      if (lx >= 0 && lx < CHUNK_SIZE && lz >= 0 && lz < CHUNK_SIZE) {
        skyL = chunk.getSkyLight(lx, ly, lz);
        blockL = chunk.getBlockLight(lx, ly, lz);
      } else {
        const ncx = chunk.cx + (lx < 0 ? -1 : lx >= CHUNK_SIZE ? 1 : 0);
        const ncz = chunk.cz + (lz < 0 ? -1 : lz >= CHUNK_SIZE ? 1 : 0);
        const neighbor = this.getChunk(ncx, ncz);
        if (neighbor) {
          const nlx = ((lx % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
          const nlz = ((lz % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
          skyL = neighbor.getSkyLight(nlx, ly, nlz);
          blockL = neighbor.getBlockLight(nlx, ly, nlz);
        } else {
          skyL = 15; blockL = 0;
        }
      }
      return Math.max(blockL, Math.floor(skyL * skyBri));
    };

    const directions = [
      { dir: [0, 1, 0], face: 'top' as const, corners: [[0,1,0],[0,1,1],[1,1,1],[1,1,0]], normal: [0,1,0] },
      { dir: [0, -1, 0], face: 'bottom' as const, corners: [[0,0,0],[1,0,0],[1,0,1],[0,0,1]], normal: [0,-1,0] },
      { dir: [1, 0, 0], face: 'side' as const, corners: [[1,0,0],[1,1,0],[1,1,1],[1,0,1]], normal: [1,0,0] },
      { dir: [-1, 0, 0], face: 'side' as const, corners: [[0,0,1],[0,1,1],[0,1,0],[0,0,0]], normal: [-1,0,0] },
      { dir: [0, 0, 1], face: 'side' as const, corners: [[1,0,1],[1,1,1],[0,1,1],[0,0,1]], normal: [0,0,1] },
      { dir: [0, 0, -1], face: 'side' as const, corners: [[0,0,0],[0,1,0],[1,1,0],[1,0,0]], normal: [0,0,-1] },
    ];

    const meshMaxY = Math.min(chunk.maxY + 1, CHUNK_HEIGHT);
    for (let y = 0; y <= meshMaxY; y++) {
      for (let x = 0; x < CHUNK_SIZE; x++) {
        for (let z = 0; z < CHUNK_SIZE; z++) {
          const blockId = chunk.getBlock(x, y, z);
          if (blockId === 0) continue;

          const block = BLOCKS[blockId];
          if (!block || !block.solid) {
            if (blockId === 7 || blockId === 40) {
              // Transparent liquid surfaces — separate bucket per type
              const above = chunk.getBlock(x, y + 1, z);
              if (above === 0) {
                const bucket = getSpecialBucket(`${blockId}-top`);
                const idx = bucket.positions.length / 3;
                const yOff = blockId === 7 ? 0.85 : 0.9;
                const corners = [[0,yOff,1],[1,yOff,1],[1,yOff,0],[0,yOff,0]];
                for (const c of corners) {
                  bucket.positions.push(x + c[0], y + c[1], z + c[2]);
                  bucket.normals.push(0, 1, 0);
                }
                bucket.indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
              }
            } else if (blockId === 47) {
              // Portal fill — crossed quads (X-shape)
              const cx2 = x + 0.5, cz2 = z + 0.5;
              // Find portal color from portalData
              const worldX = chunk.cx * CHUNK_SIZE + x;
              const worldZ = chunk.cz * CHUNK_SIZE + z;
              let colorIdx = -1;
              for (const p of this.portalData) {
                // Check if this block is a fill position for this portal
                if (p.orientation === 'x') {
                  if (worldZ === p.originZ && worldX === p.originX + 1 &&
                      (y === p.originY + 1 || y === p.originY + 2)) {
                    colorIdx = p.colorIndex;
                    break;
                  }
                } else {
                  if (worldX === p.originX && worldZ === p.originZ + 1 &&
                      (y === p.originY + 1 || y === p.originY + 2)) {
                    colorIdx = p.colorIndex;
                    break;
                  }
                }
              }
              const matKey = `portal-${colorIdx}`;
              if (!specialMats[matKey]) {
                specialMats[matKey] = colorIdx >= 0 && colorIdx < portalMats.length
                  ? portalMats[colorIdx]
                  : portalMatDefault;
              }
              const pd = getSpecialBucket(matKey);
              // Two crossed quads forming an X
              const quads = [
                // Diagonal 1: from (0,0,0) to (1,1,1)
                [[x, y, z], [x, y + 1, z], [x + 1, y + 1, z + 1], [x + 1, y, z + 1]],
                // Diagonal 2: from (1,0,0) to (0,1,1)
                [[x + 1, y, z], [x + 1, y + 1, z], [x, y + 1, z + 1], [x, y, z + 1]],
              ];
              for (const quad of quads) {
                const idx = pd.positions.length / 3;
                for (const c of quad) {
                  pd.positions.push(c[0], c[1], c[2]);
                  pd.normals.push(0, 0, 1);
                }
                pd.indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
              }
            } else if (blockId >= 51 && blockId <= 54) {
              // Paint blocks — crossed quads (like portal fill)
              const matKey = `paint-${blockId}`;
              const pd = getSpecialBucket(matKey);
              const quads = [
                [[x, y, z], [x, y + 1, z], [x + 1, y + 1, z + 1], [x + 1, y, z + 1]],
                [[x + 1, y, z], [x + 1, y + 1, z], [x, y + 1, z + 1], [x, y, z + 1]],
              ];
              for (const quad of quads) {
                const idx = pd.positions.length / 3;
                for (const c of quad) {
                  pd.positions.push(c[0], c[1], c[2]);
                  pd.normals.push(0, 0, 1);
                }
                pd.indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
              }
            } else if (blockId === 20) {
              // Torch — special geometry
              const s = 0.0625;
              const cx2 = x + 0.5, cz2 = z + 0.5;
              const stickH = 0.625;
              const stickFaces = [
                { corners: [[cx2-s,y,cz2-s],[cx2-s,y+stickH,cz2-s],[cx2+s,y+stickH,cz2-s],[cx2+s,y,cz2-s]], normal: [0,0,-1] },
                { corners: [[cx2+s,y,cz2+s],[cx2+s,y+stickH,cz2+s],[cx2-s,y+stickH,cz2+s],[cx2-s,y,cz2+s]], normal: [0,0,1] },
                { corners: [[cx2+s,y,cz2-s],[cx2+s,y+stickH,cz2-s],[cx2+s,y+stickH,cz2+s],[cx2+s,y,cz2+s]], normal: [1,0,0] },
                { corners: [[cx2-s,y,cz2+s],[cx2-s,y+stickH,cz2+s],[cx2-s,y+stickH,cz2-s],[cx2-s,y,cz2-s]], normal: [-1,0,0] },
              ];
              const stickData = getSpecialBucket('20-stick');
              for (const sf of stickFaces) {
                const idx = stickData.positions.length / 3;
                for (const c of sf.corners) {
                  stickData.positions.push(c[0], c[1], c[2]);
                  stickData.normals.push(sf.normal[0], sf.normal[1], sf.normal[2]);
                }
                stickData.indices.push(idx, idx+1, idx+2, idx, idx+2, idx+3);
              }
              const f = 0.1;
              const flameBot = y + stickH;
              const flameTopY = y + stickH + 0.25;
              const flameFaces = [
                { corners: [[cx2-f,flameBot,cz2-f],[cx2-f,flameTopY,cz2-f],[cx2+f,flameTopY,cz2-f],[cx2+f,flameBot,cz2-f]], normal: [0,0,-1] },
                { corners: [[cx2+f,flameBot,cz2+f],[cx2+f,flameTopY,cz2+f],[cx2-f,flameTopY,cz2+f],[cx2-f,flameBot,cz2+f]], normal: [0,0,1] },
                { corners: [[cx2+f,flameBot,cz2-f],[cx2+f,flameTopY,cz2-f],[cx2+f,flameTopY,cz2+f],[cx2+f,flameBot,cz2+f]], normal: [1,0,0] },
                { corners: [[cx2-f,flameBot,cz2+f],[cx2-f,flameTopY,cz2+f],[cx2-f,flameTopY,cz2-f],[cx2-f,flameBot,cz2-f]], normal: [-1,0,0] },
                { corners: [[cx2-f,flameTopY,cz2-f],[cx2-f,flameTopY,cz2+f],[cx2+f,flameTopY,cz2+f],[cx2+f,flameTopY,cz2-f]], normal: [0,1,0] },
              ];
              const flameData = getSpecialBucket('20-flame');
              for (const ff of flameFaces) {
                const idx = flameData.positions.length / 3;
                for (const c of ff.corners) {
                  flameData.positions.push(c[0], c[1], c[2]);
                  flameData.normals.push(ff.normal[0], ff.normal[1], ff.normal[2]);
                }
                flameData.indices.push(idx, idx+1, idx+2, idx, idx+2, idx+3);
              }
            }
            continue;
          }

          // Bed blocks: render as half-height slabs with blanket + pillow
          if (blockId === 48 || blockId === 49) {
            const h = 0.5; // slab height
            const pillow = blockId === 49; // head part gets a pillow

            // Lightmap: beds are half-height, sample light above
            const bedLight = getLightAt(x, y + 1, z);
            const bedBri = LIGHT_MULTIPLIER[bedLight];

            // Colors (modulated by lightmap)
            const frameR = 0.545 * bedBri, frameG = 0.412 * bedBri, frameB = 0.078 * bedBri;
            const topR = (blockId === 48 ? 0.8 : 0.667) * bedBri;
            const topG = (blockId === 48 ? 0.2 : 0.133) * bedBri;
            const topB = (blockId === 48 ? 0.2 : 0.133) * bedBri;
            const botR = frameR, botG = frameG, botB = frameB;

            // Helper to push a colored quad into opaque arrays
            function addBedFace(corners: number[][], normal: number[], r: number, g: number, b: number) {
              const idx = opaquePos.length / 3;
              for (const c of corners) {
                opaquePos.push(c[0], c[1], c[2]);
                opaqueNorm.push(normal[0], normal[1], normal[2]);
                opaqueColor.push(r, g, b);
              }
              opaqueIdx.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
            }

            // Top face (blanket)
            addBedFace(
              [[x, y + h, z], [x, y + h, z + 1], [x + 1, y + h, z + 1], [x + 1, y + h, z]],
              [0, 1, 0], topR, topG, topB);
            // Bottom face
            addBedFace(
              [[x, y, z], [x + 1, y, z], [x + 1, y, z + 1], [x, y, z + 1]],
              [0, -1, 0], botR, botG, botB);
            // Side faces (frame)
            addBedFace(
              [[x + 1, y, z], [x + 1, y + h, z], [x + 1, y + h, z + 1], [x + 1, y, z + 1]],
              [1, 0, 0], frameR, frameG, frameB);
            addBedFace(
              [[x, y, z + 1], [x, y + h, z + 1], [x, y + h, z], [x, y, z]],
              [-1, 0, 0], frameR, frameG, frameB);
            addBedFace(
              [[x + 1, y, z + 1], [x + 1, y + h, z + 1], [x, y + h, z + 1], [x, y, z + 1]],
              [0, 0, 1], frameR, frameG, frameB);
            addBedFace(
              [[x, y, z], [x, y + h, z], [x + 1, y + h, z], [x + 1, y, z]],
              [0, 0, -1], frameR, frameG, frameB);

            // Pillow on head part: a small raised white bump
            if (pillow) {
              const pw = 0.7, pd = 0.35, ph = 0.15;
              const px = x + (1 - pw) / 2;
              const pz = z + (1 - pd) / 2;
              const py = y + h;
              const pR = 0.93 * bedBri, pG = 0.93 * bedBri, pB = 0.93 * bedBri; // white pillow

              addBedFace(
                [[px, py + ph, pz], [px, py + ph, pz + pd], [px + pw, py + ph, pz + pd], [px + pw, py + ph, pz]],
                [0, 1, 0], pR, pG, pB);
              addBedFace(
                [[px, py, pz], [px, py + ph, pz], [px + pw, py + ph, pz], [px + pw, py, pz]],
                [0, 0, -1], pR, pG, pB);
              addBedFace(
                [[px + pw, py, pz + pd], [px + pw, py + ph, pz + pd], [px, py + ph, pz + pd], [px, py, pz + pd]],
                [0, 0, 1], pR, pG, pB);
              addBedFace(
                [[px, py, pz + pd], [px, py + ph, pz + pd], [px, py + ph, pz], [px, py, pz]],
                [-1, 0, 0], pR, pG, pB);
              addBedFace(
                [[px + pw, py, pz], [px + pw, py + ph, pz], [px + pw, py + ph, pz + pd], [px + pw, py, pz + pd]],
                [1, 0, 0], pR, pG, pB);
            }

            continue; // skip normal opaque rendering
          }

          // Vliegblok / Supervliegblok: arrow symbols on top — special bucket
          if (blockId === 44 || blockId === 45) {
            const above = chunk.getBlock(x, y + 1, z);
            const aboveDef = BLOCKS[above];
            if (above === 0 || (aboveDef && aboveDef.transparent)) {
              const arrowKey = blockId === 45 ? '45-arrow' : '44-arrow';
              const ad = getSpecialBucket(arrowKey);
              const yTop = y + 1.01;
              const cx2 = x + 0.5, cz2 = z + 0.5;

              if (blockId === 44) {
                const triVerts = [
                  [cx2, yTop, cz2 - 0.3],
                  [cx2 - 0.25, yTop, cz2 + 0.05],
                  [cx2 + 0.25, yTop, cz2 + 0.05],
                ];
                const idx = ad.positions.length / 3;
                for (const v of triVerts) {
                  ad.positions.push(v[0], v[1], v[2]);
                  ad.normals.push(0, 1, 0);
                }
                ad.indices.push(idx, idx + 1, idx + 2);

                const barW = 0.08;
                const barCorners = [
                  [cx2 - barW, yTop, cz2 + 0.05],
                  [cx2 - barW, yTop, cz2 + 0.3],
                  [cx2 + barW, yTop, cz2 + 0.3],
                  [cx2 + barW, yTop, cz2 + 0.05],
                ];
                const idx2 = ad.positions.length / 3;
                for (const c of barCorners) {
                  ad.positions.push(c[0], c[1], c[2]);
                  ad.normals.push(0, 1, 0);
                }
                ad.indices.push(idx2, idx2 + 1, idx2 + 2, idx2, idx2 + 2, idx2 + 3);
              } else {
                const offsets = [-0.25, 0.0, 0.25];
                for (const oz of offsets) {
                  const triVerts = [
                    [cx2, yTop, cz2 - 0.15 + oz],
                    [cx2 - 0.2, yTop, cz2 + 0.1 + oz],
                    [cx2 + 0.2, yTop, cz2 + 0.1 + oz],
                  ];
                  const idx = ad.positions.length / 3;
                  for (const v of triVerts) {
                    ad.positions.push(v[0], v[1], v[2]);
                    ad.normals.push(0, 1, 0);
                  }
                  ad.indices.push(idx, idx + 1, idx + 2);
                }
              }
            }
          }

          // Speed block: double chevron >> on top
          if (blockId === 63) {
            const above = chunk.getBlock(x, y + 1, z);
            const aboveDef = BLOCKS[above];
            if (above === 0 || (aboveDef && aboveDef.transparent)) {
              const ad = getSpecialBucket('63-symbol');
              const yTop = y + 1.01;
              const cx2 = x + 0.5, cz2 = z + 0.5;
              // Two chevrons pointing in -Z direction
              for (const oz of [-0.12, 0.12]) {
                const triVerts = [
                  [cx2, yTop, cz2 - 0.15 + oz],
                  [cx2 - 0.2, yTop, cz2 + 0.1 + oz],
                  [cx2 + 0.2, yTop, cz2 + 0.1 + oz],
                ];
                const idx = ad.positions.length / 3;
                for (const v of triVerts) { ad.positions.push(v[0], v[1], v[2]); ad.normals.push(0, 1, 0); }
                ad.indices.push(idx, idx + 1, idx + 2);
              }
            }
          }

          // Launch pad: star symbol on top
          if (blockId === 50) {
            const above = chunk.getBlock(x, y + 1, z);
            const aboveDef = BLOCKS[above];
            if (above === 0 || (aboveDef && aboveDef.transparent)) {
              const ad = getSpecialBucket('50-symbol');
              const yTop = y + 1.01;
              const cx2 = x + 0.5, cz2 = z + 0.5;
              // Star: 4 triangles pointing outward
              const dirs = [[0, -0.3], [0.3, 0], [0, 0.3], [-0.3, 0]];
              for (const [dx, dz] of dirs) {
                const triVerts = [
                  [cx2 + dx, yTop, cz2 + dz],
                  [cx2 + dz * 0.3, yTop, cz2 - dx * 0.3],
                  [cx2 - dz * 0.3, yTop, cz2 + dx * 0.3],
                ];
                const idx = ad.positions.length / 3;
                for (const v of triVerts) { ad.positions.push(v[0], v[1], v[2]); ad.normals.push(0, 1, 0); }
                ad.indices.push(idx, idx + 1, idx + 2);
              }
            }
          }

          // Arrow blocks: directional arrow on top (55=N, 56=E, 57=S, 58=W)
          if (blockId >= 55 && blockId <= 58) {
            const above = chunk.getBlock(x, y + 1, z);
            const aboveDef = BLOCKS[above];
            if (above === 0 || (aboveDef && aboveDef.transparent)) {
              const ad = getSpecialBucket('55-arrow');
              const yTop = y + 1.01;
              const cx2 = x + 0.5, cz2 = z + 0.5;
              // Arrow triangle and shaft, rotated by direction
              // blockId 55=N(-Z), 56=E(+X), 57=S(+Z), 58=W(-X)
              const angles: Record<number, number> = { 55: 0, 56: -Math.PI/2, 57: Math.PI, 58: Math.PI/2 };
              const angle = angles[blockId] || 0;
              const cos = Math.cos(angle), sin = Math.sin(angle);
              const rotate = (px: number, pz: number): [number, number] => [cx2 + px * cos - pz * sin, cz2 + px * sin + pz * cos];

              // Triangle tip
              const [t1x, t1z] = rotate(0, -0.3);
              const [t2x, t2z] = rotate(-0.2, 0.0);
              const [t3x, t3z] = rotate(0.2, 0.0);
              const idx = ad.positions.length / 3;
              ad.positions.push(t1x, yTop, t1z, t2x, yTop, t2z, t3x, yTop, t3z);
              ad.normals.push(0, 1, 0, 0, 1, 0, 0, 1, 0);
              ad.indices.push(idx, idx + 1, idx + 2);

              // Shaft
              const w = 0.06;
              const [s1x, s1z] = rotate(-w, 0.0);
              const [s2x, s2z] = rotate(-w, 0.25);
              const [s3x, s3z] = rotate(w, 0.25);
              const [s4x, s4z] = rotate(w, 0.0);
              const idx2 = ad.positions.length / 3;
              ad.positions.push(s1x, yTop, s1z, s2x, yTop, s2z, s3x, yTop, s3z, s4x, yTop, s4z);
              ad.normals.push(0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0);
              ad.indices.push(idx2, idx2 + 1, idx2 + 2, idx2, idx2 + 2, idx2 + 3);
            }
          }

          // Music block: note symbol on top and all 4 sides
          if (blockId === 59) {
            const ad = getSpecialBucket('59-symbol');

            // Top face
            const above = chunk.getBlock(x, y + 1, z);
            const aboveDef = BLOCKS[above];
            if (above === 0 || (aboveDef && aboveDef.transparent)) {
              const yTop = y + 1.01;
              const cx2 = x + 0.5, cz2 = z + 0.5;
              // Note head (diamond)
              const r = 0.12;
              const idx = ad.positions.length / 3;
              ad.positions.push(cx2 - 0.1, yTop, cz2 + 0.1, cx2 - 0.1 - r, yTop, cz2 + 0.1, cx2 - 0.1, yTop, cz2 + 0.1 + r, cx2 - 0.1 + r, yTop, cz2 + 0.1);
              ad.normals.push(0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0);
              ad.indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
              // Stem
              const w = 0.03;
              const idx2 = ad.positions.length / 3;
              ad.positions.push(cx2 - 0.1 + r - w, yTop, cz2 + 0.1, cx2 - 0.1 + r - w, yTop, cz2 - 0.2, cx2 - 0.1 + r + w, yTop, cz2 - 0.2, cx2 - 0.1 + r + w, yTop, cz2 + 0.1);
              ad.normals.push(0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0);
              ad.indices.push(idx2, idx2 + 1, idx2 + 2, idx2, idx2 + 2, idx2 + 3);
            }

            // Side faces: +X, -X, +Z, -Z
            const cx2 = x + 0.5, cy2 = y + 0.5, cz2 = z + 0.5;
            const faces = [
              { nx: 1, nz: 0, check: chunk.getBlock(x + 1, y, z) },   // +X
              { nx: -1, nz: 0, check: chunk.getBlock(x - 1, y, z) },  // -X
              { nx: 0, nz: 1, check: chunk.getBlock(x, y, z + 1) },   // +Z
              { nx: 0, nz: -1, check: chunk.getBlock(x, y, z - 1) },  // -Z
            ];
            for (const face of faces) {
              const fb = face.check;
              const fbDef = BLOCKS[fb];
              if (fb !== 0 && fbDef && !fbDef.transparent) continue;

              const off = 0.505;
              // Note head (small quad on the side face)
              const r2 = 0.1;
              const headCy = cy2 - 0.05;
              if (face.nx !== 0) {
                // Face on X side: note in YZ plane
                const fx = cx2 + face.nx * off;
                const headZ = cz2 + 0.05;
                const idx3 = ad.positions.length / 3;
                ad.positions.push(
                  fx, headCy - r2, headZ,  fx, headCy, headZ - r2,
                  fx, headCy + r2, headZ,  fx, headCy, headZ + r2
                );
                ad.normals.push(face.nx, 0, 0, face.nx, 0, 0, face.nx, 0, 0, face.nx, 0, 0);
                ad.indices.push(idx3, idx3 + 1, idx3 + 2, idx3, idx3 + 2, idx3 + 3);
                // Stem
                const sw = 0.025;
                const idx4 = ad.positions.length / 3;
                ad.positions.push(
                  fx, headCy + r2, headZ - sw,  fx, cy2 + 0.3, headZ - sw,
                  fx, cy2 + 0.3, headZ + sw,    fx, headCy + r2, headZ + sw
                );
                ad.normals.push(face.nx, 0, 0, face.nx, 0, 0, face.nx, 0, 0, face.nx, 0, 0);
                ad.indices.push(idx4, idx4 + 1, idx4 + 2, idx4, idx4 + 2, idx4 + 3);
              } else {
                // Face on Z side: note in XY plane
                const fz = cz2 + face.nz * off;
                const headX = cx2 - 0.05;
                const idx3 = ad.positions.length / 3;
                ad.positions.push(
                  headX, headCy - r2, fz,  headX - r2, headCy, fz,
                  headX, headCy + r2, fz,  headX + r2, headCy, fz
                );
                ad.normals.push(0, 0, face.nz, 0, 0, face.nz, 0, 0, face.nz, 0, 0, face.nz);
                ad.indices.push(idx3, idx3 + 1, idx3 + 2, idx3, idx3 + 2, idx3 + 3);
                // Stem
                const sw = 0.025;
                const idx4 = ad.positions.length / 3;
                ad.positions.push(
                  headX - sw, headCy + r2, fz,  headX - sw, cy2 + 0.3, fz,
                  headX + sw, cy2 + 0.3, fz,    headX + sw, headCy + r2, fz
                );
                ad.normals.push(0, 0, face.nz, 0, 0, face.nz, 0, 0, face.nz, 0, 0, face.nz);
                ad.indices.push(idx4, idx4 + 1, idx4 + 2, idx4, idx4 + 2, idx4 + 3);
              }
            }
          }

          // Teleport block: swirl/diamond on top
          if (blockId === 60) {
            const above = chunk.getBlock(x, y + 1, z);
            const aboveDef = BLOCKS[above];
            if (above === 0 || (aboveDef && aboveDef.transparent)) {
              const ad = getSpecialBucket('60-symbol');
              const yTop = y + 1.01;
              const cx2 = x + 0.5, cz2 = z + 0.5;
              // Diamond shape
              const idx = ad.positions.length / 3;
              ad.positions.push(cx2, yTop, cz2 - 0.3, cx2 - 0.3, yTop, cz2, cx2, yTop, cz2 + 0.3, cx2 + 0.3, yTop, cz2);
              ad.normals.push(0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0);
              ad.indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
            }
          }

          // TNT: X symbol on top
          if (blockId === 61) {
            const above = chunk.getBlock(x, y + 1, z);
            const aboveDef = BLOCKS[above];
            if (above === 0 || (aboveDef && aboveDef.transparent)) {
              const ad = getSpecialBucket('61-symbol');
              const yTop = y + 1.01;
              const cx2 = x + 0.5, cz2 = z + 0.5;
              const w = 0.06;
              // X diagonal bars
              for (const [dx, dz] of [[1, 1], [1, -1]]) {
                const idx = ad.positions.length / 3;
                ad.positions.push(
                  cx2 - 0.25 * dx - w * dz, yTop, cz2 - 0.25 * dz + w * dx,
                  cx2 + 0.25 * dx - w * dz, yTop, cz2 + 0.25 * dz + w * dx,
                  cx2 + 0.25 * dx + w * dz, yTop, cz2 + 0.25 * dz - w * dx,
                  cx2 - 0.25 * dx + w * dz, yTop, cz2 - 0.25 * dz - w * dx
                );
                ad.normals.push(0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0);
                ad.indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
              }
            }
          }

          // Firework: rocket/star on top
          if (blockId === 62) {
            const above = chunk.getBlock(x, y + 1, z);
            const aboveDef = BLOCKS[above];
            if (above === 0 || (aboveDef && aboveDef.transparent)) {
              const ad = getSpecialBucket('62-symbol');
              const yTop = y + 1.01;
              const cx2 = x + 0.5, cz2 = z + 0.5;
              // Triangle (rocket shape)
              const idx = ad.positions.length / 3;
              ad.positions.push(cx2, yTop, cz2 - 0.3, cx2 - 0.15, yTop, cz2 + 0.2, cx2 + 0.15, yTop, cz2 + 0.2);
              ad.normals.push(0, 1, 0, 0, 1, 0, 0, 1, 0);
              ad.indices.push(idx, idx + 1, idx + 2);
            }
          }

          // Transparent solid blocks (glass, ice) get their own bucket
          const isTransparentSolid = block.transparent;

          for (const { dir, face, corners, normal } of directions) {
            const nx = x + dir[0];
            const ny = y + dir[1];
            const nz = z + dir[2];

            let neighborBlock: number;
            if (nx < 0 || nx >= CHUNK_SIZE || nz < 0 || nz >= CHUNK_SIZE) {
              const wcx = chunk.cx * CHUNK_SIZE + nx;
              const wcz = chunk.cz * CHUNK_SIZE + nz;
              neighborBlock = this.getBlock(wcx, ny, wcz);
            } else {
              neighborBlock = chunk.getBlock(nx, ny, nz);
            }

            const neighborDef = BLOCKS[neighborBlock];
            if (neighborBlock === 0 || (neighborDef && neighborDef.transparent)) {
              if (isTransparentSolid) {
                // Transparent solid — separate bucket per block type
                const bucket = getSpecialBucket(`trans-${blockId}-${face}`);
                const idx = bucket.positions.length / 3;
                for (const c of corners) {
                  bucket.positions.push(x + c[0], y + c[1], z + c[2]);
                  bucket.normals.push(normal[0], normal[1], normal[2]);
                }
                bucket.indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
              } else {
                // Opaque block — merge into single geometry with vertex colors
                let color = block.color;
                if (face === 'top' && block.topColor) color = block.topColor;
                if (face === 'bottom' && block.bottomColor) color = block.bottomColor;
                tmpColor.set(color);

                // Lightmap: sample light at the neighbor air position this face is exposed to
                const effectiveLight = getLightAt(nx, ny, nz);
                const brightness = LIGHT_MULTIPLIER[effectiveLight];
                const r = tmpColor.r * brightness;
                const g = tmpColor.g * brightness;
                const b = tmpColor.b * brightness;

                const idx = opaquePos.length / 3;
                for (const c of corners) {
                  opaquePos.push(x + c[0], y + c[1], z + c[2]);
                  opaqueNorm.push(normal[0], normal[1], normal[2]);
                  opaqueColor.push(r, g, b);
                }
                opaqueIdx.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
              }
            }
          }
        }
      }
    }

    // Build merged opaque mesh (single draw call for all opaque blocks)
    if (opaquePos.length > 0) {
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(opaquePos, 3));
      geometry.setAttribute('normal', new THREE.Float32BufferAttribute(opaqueNorm, 3));
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(opaqueColor, 3));
      geometry.setIndex(opaqueIdx);
      const mesh = new THREE.Mesh(geometry, opaqueVertexMat);
      group.add(mesh);
    }

    // Build special/transparent meshes
    for (const [key, data] of specialBuckets) {
      if (data.positions.length === 0) continue;

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(data.positions, 3));
      geometry.setAttribute('normal', new THREE.Float32BufferAttribute(data.normals, 3));
      geometry.setIndex(data.indices);

      let material: THREE.MeshLambertMaterial;
      if (specialMats[key]) {
        material = specialMats[key];
      } else if (key.startsWith('trans-')) {
        // Parse blockId and face from key: "trans-{blockId}-{face}"
        const parts = key.split('-');
        const bId = parseInt(parts[1]);
        const face = parts[2] as 'top' | 'bottom' | 'side';
        material = getBlockMaterial(bId, face);
      } else {
        // Liquid surfaces (7-top, 40-top)
        const bId = parseInt(key);
        material = getBlockMaterial(bId, 'top');
      }
      const mesh = new THREE.Mesh(geometry, material);
      group.add(mesh);
    }

    chunk.mesh = group;
    chunk.dirty = false;
    this.scene.add(group);
  }

  dispose() {
    for (const [, chunk] of this.chunks) {
      if (chunk.mesh) {
        this.scene.remove(chunk.mesh);
        chunk.mesh.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.geometry.dispose();
          }
        });
      }
    }
    this.chunks.clear();
    this.requestedChunks.clear();
  }

  getHeight(wx: number, wz: number): number {
    const cx = Math.floor(wx / CHUNK_SIZE);
    const cz = Math.floor(wz / CHUNK_SIZE);
    const chunk = this.getChunk(cx, cz);
    const startY = chunk ? chunk.maxY : CHUNK_HEIGHT - 1;
    for (let y = startY; y >= 0; y--) {
      const block = this.getBlock(wx, y, wz);
      if (block !== 0 && BLOCKS[block]?.solid) return y + 1;
    }
    return SEA_LEVEL;
  }

  getLightSources(playerX: number, playerZ: number, radius: number): { x: number; y: number; z: number; level: number; blockId: number }[] {
    const sources: { x: number; y: number; z: number; level: number; blockId: number }[] = [];
    const pcx = Math.floor(playerX / CHUNK_SIZE);
    const pcz = Math.floor(playerZ / CHUNK_SIZE);
    const chunkRadius = Math.ceil(radius / CHUNK_SIZE);

    for (let dx = -chunkRadius; dx <= chunkRadius; dx++) {
      for (let dz = -chunkRadius; dz <= chunkRadius; dz++) {
        const chunk = this.getChunk(pcx + dx, pcz + dz);
        if (!chunk) continue;
        const baseX = chunk.cx * CHUNK_SIZE;
        const baseZ = chunk.cz * CHUNK_SIZE;
        const maxY = Math.min(chunk.maxY + 1, CHUNK_HEIGHT);
        for (let y = 0; y <= maxY; y++) {
          for (let x = 0; x < CHUNK_SIZE; x++) {
            for (let z = 0; z < CHUNK_SIZE; z++) {
              const blockId = chunk.getBlock(x, y, z);
              if (blockId === 0) continue;
              const block = BLOCKS[blockId];
              if (block && block.lightLevel && block.lightLevel > 0) {
                sources.push({ x: baseX + x, y, z: baseZ + z, level: block.lightLevel, blockId });
              }
            }
          }
        }
      }
    }
    return sources;
  }

  update(playerX: number, playerZ: number, renderDistance: number = 6) {
    const pcx = Math.floor(playerX / CHUNK_SIZE);
    const pcz = Math.floor(playerZ / CHUNK_SIZE);

    // Request chunks near player from server
    let rebuildsThisFrame = 0;
    const MAX_REBUILDS_PER_FRAME = 4;

    for (let dx = -renderDistance; dx <= renderDistance; dx++) {
      for (let dz = -renderDistance; dz <= renderDistance; dz++) {
        if (dx * dx + dz * dz > renderDistance * renderDistance) continue;
        const cx = pcx + dx;
        const cz = pcz + dz;
        const key = this.chunkKey(cx, cz);

        if (!this.chunks.has(key) && !this.requestedChunks.has(key)) {
          this.requestedChunks.add(key);
          this.onRequestChunk?.(cx, cz);
        }

        const chunk = this.chunks.get(key);
        if (chunk && chunk.dirty) {
          if (rebuildsThisFrame < MAX_REBUILDS_PER_FRAME) {
            this.buildChunkMesh(chunk);
            rebuildsThisFrame++;
          }
        }
      }
    }

    // Remove far chunks
    for (const [key, chunk] of this.chunks) {
      const dx = chunk.cx - pcx;
      const dz = chunk.cz - pcz;
      if (dx * dx + dz * dz > (renderDistance + 2) * (renderDistance + 2)) {
        if (chunk.mesh) {
          this.scene.remove(chunk.mesh);
          chunk.mesh.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              child.geometry.dispose();
            }
          });
        }
        this.chunks.delete(key);
      }
    }
  }
}
